import {
  ViewBooking,
  ViewBookingStatusEnum,
  ViewBookingType
} from "collins-js-sdk";
import { ChevronLeft, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { addOrUpdateBooking } from "../../../../../APIs/Booking/UpdateBooking";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useNotificationsContext } from "../../../../../hooks/useNotificationsContext";
import { WaitlistSlot } from "../../../../../types/areaAvailability";
import {
  isBookingValid,
  isNonWalkInBookingValid,
  isSameCollinsDay
} from "../../../../../utils/booking";
import { getTimeToNextQuarterHour } from "../../../../../utils/helpers";
import { EDSButton } from "../../../../eds/EDSButton";
import {
  AddBookingState,
  ButtonType,
  clearAllHighlightedAreas,
  defaultState,
  getDefaultDuration,
  toggleHighlightArea
} from "./AddBookingController";
import AddBookingStep1 from "./AddBookingStep1";
import AddBookingStep2 from "./AddBookingStep2";

export type AddBookingType = {
  prefillBookingType: ViewBookingType | null | undefined;
  setPrefillBookingType: (
    prefillBookingType: ViewBookingType | null | undefined
  ) => void;
  setShowAddBooking: (show: boolean) => void;
};
const AddBooking = ({
  setShowAddBooking,
  prefillBookingType,
  setPrefillBookingType
}: AddBookingType) => {
  const bookingContext = useDiaryContext();
  const notificationsContext = useNotificationsContext();

  const defaultBooking = {
    ...defaultState.booking,
    type: prefillBookingType,
    numPeople:
      (prefillBookingType as any)?.numPeople || defaultState.booking.numPeople
  };
  const date = bookingContext.date;
  const time = getTimeToNextQuarterHour();
  const duration = prefillBookingType
    ? getDefaultDuration(bookingContext, defaultBooking)
    : null;
  const [state, setState] = useState<AddBookingState>({
    ...defaultState,
    booking: {
      ...defaultBooking,
      duration,
      time,
      date,
      actualGuests:
        (prefillBookingType as any)?.numPeople || defaultBooking.numPeople
    }
  });

  const [fullScreen, setFullScreen] = useState<React.JSX.Element>();
  const previousVenueIdRef = useRef<string | undefined>(
    bookingContext.venueConfig?._id
  );

  const waitlisted = state.booking.waitlisted;
  const walkIn = state.booking.walkIn;
  useEffect(() => {
    setState((prev) => {
      const nextMaxSteps =
        !prev.booking.walkIn || prev.booking.waitlisted ? 2 : 1;
      return prev.maxSteps === nextMaxSteps
        ? prev
        : { ...prev, maxSteps: nextMaxSteps };
    });
  }, [waitlisted, walkIn]);

  useEffect(() => {
    clearAllHighlightedAreas();
    toggleHighlightArea("unassigned");
    return () => {
      clearAllHighlightedAreas();
      bookingContext.setDiaryContext({ gridAreaClick: undefined });
    };
  }, []);

  // Clear booking type and assigned areas when venue changes
  useEffect(() => {
    const currentVenueId = bookingContext.venueConfig?._id;
    const previousVenueId = previousVenueIdRef.current;

    // Initial load - just track the venue ID
    if (currentVenueId && !previousVenueId) {
      previousVenueIdRef.current = currentVenueId;
      return;
    }

    // Only proceed if venue has actually changed
    if (!currentVenueId || currentVenueId === previousVenueId) {
      return;
    }

    // Check if current booking type exists in the new venue
    const currentBookingType = state.booking.type;
    const bookingTypeExists =
      currentBookingType &&
      bookingContext.venueConfig?.booking_types?.some(
        (bt) => bt.id === currentBookingType.id
      );

    // Always clear assigned areas when venue changes
    // Also clear booking type if it doesn't exist in the new venue
    setState((prev) => ({
      ...prev,
      booking: {
        ...prev.booking,
        type: bookingTypeExists ? prev.booking.type : null,
        assignedAreas: []
      }
    }));

    // Update the ref to track the new venue ID
    previousVenueIdRef.current = currentVenueId;
  }, [
    bookingContext.venueConfig?._id,
    bookingContext.venueConfig?.booking_types
  ]);

  const updateBooking = (bookingProps: Partial<ViewBooking>) => {
    const booking = { ...state.booking, ...bookingProps };
    setState({ ...state, booking });
  };

  const handleWaitlistResponse = (waitlistSlot?: WaitlistSlot) => {
    setState({
      ...state,
      waitlistSlotMins: waitlistSlot?.minutes_until_available ?? 0
    });
  };

  const confirmBookingCreation = () => {
    createBooking();
  };

  const createBooking = async (bookingProps?: Partial<ViewBooking>) => {
    let booking = { ...state.booking, ...bookingProps };

    if (state.step === 1) {
      const bookingCustomer = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        partnerSource: ""
      };
      booking = { ...booking, ...bookingCustomer };
    }

    if (walkIn && !waitlisted) {
      booking.currentStage =
        bookingContext.stages?.find((stage) => stage.walk_in_default)?.id ?? "";
    }

    if (waitlisted) {
      booking.walkIn = false;
      booking.status = ViewBookingStatusEnum.InProgress;
      booking.time = null;
      booking.currentStage = null;
      booking.assignedAreas = [];
    }
    booking.venueGroup = bookingContext.venueConfig?.venue_group;
    booking.venueId = bookingContext.venueConfig?._id;
    bookingContext.setDiaryContext({
      gridAreaClick: undefined,
      highlightArea: undefined
    });
    // remember last used booking type
    setPrefillBookingType(booking.type);

    setShowAddBooking(false);
    const response = await addOrUpdateBooking({
      booking,
      diaryContext: bookingContext
    });

    notificationsContext.addNotification({
      heading:
        !response || response?.unsaved ? "Update pending" : "Booking added",
      autoClose: 4000,
      toastType: "information"
    });
  };

  const formatWaitlistTime = (minutes: number): string => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMins = minutes % 60;
      if (remainingMins === 0) {
        return hours === 1 ? "1 hour" : `${hours} hours`;
      }
      const hourText = hours === 1 ? "1 hour" : `${hours} hours`;
      const minText = remainingMins === 1 ? "1 min" : `${remainingMins} mins`;
      return `${hourText} ${minText}`;
    }
    return minutes === 1 ? "1 min" : `${minutes} mins`;
  };

  const isBookingDateToday = (): boolean => {
    const bookingDate = state.booking.date
      ? new Date(state.booking.date)
      : null;
    if (!bookingDate) return false;

    return isSameCollinsDay(new Date(), bookingDate);
  };

  const getSecondaryButton = (): ButtonType => {
    let text = "";
    let visible = false;
    let disabled = isNonWalkInBookingValid(state.booking);
    let onClick: () => void = confirmBookingCreation;

    if (state.step === 1 && waitlisted) {
      text = "Remove from waitlist";
      visible = true;
      disabled = false;
      onClick = () => {
        setState({
          ...state,
          booking: { ...state.booking, waitlisted: false }
        });
      };
    }

    // Add to waitlist button - aligned with collins-ui logic:
    // Only show for advanced waitlist venues when date is TODAY
    // Button is disabled (not hidden) when wait time is invalid
    const advancedWaitlistEnabled =
      bookingContext.venueConfig?.advanced_waitlist_enabled ?? false;
    const maxWaitlistMinutes = 180;
    const waitlistButtonDisabled =
      !state.booking.numPeople ||
      !state.waitlistSlotMins ||
      state.waitlistSlotMins < 0 ||
      state.waitlistSlotMins > maxWaitlistMinutes;
    if (
      state.step === 1 &&
      !waitlisted &&
      advancedWaitlistEnabled &&
      isBookingDateToday()
    ) {
      text =
        state.waitlistSlotMins && state.waitlistSlotMins > 0
          ? `Add to waitlist (Est. ${formatWaitlistTime(state.waitlistSlotMins)})`
          : "Add to waitlist";
      visible = true;
      disabled = waitlistButtonDisabled;
      onClick = () => {
        setState((prev) => ({
          ...prev,
          booking: { ...prev.booking, waitlisted: true },
          maxSteps: 2,
          step: 2
        }));
      };
    }

    // Create Enquiry button - aligned with collins-ui logic:
    // Hidden when walk-in OR (today AND waitlisted)
    // Visible when NOT walk-in AND NOT (today AND waitlisted)
    if (state.step === 2 && !walkIn && !(isBookingDateToday() && waitlisted)) {
      text = "Create Enquiry";
      visible = true;
      onClick = () => {
        createBooking({
          status: ViewBookingStatusEnum.InProgress,
          actualGuests: undefined
        });
      };
    }

    return {
      text,
      visible,
      disabled,
      onClick
    };
  };

  const getPrimaryButton = (): ButtonType => {
    let text = "Create Booking";
    let disabled = isBookingValid(state.booking);
    let onClick: () => Promise<void> | void = confirmBookingCreation;

    if (state.step === 1 && state.maxSteps > 1) {
      text = "Next";
      onClick = () => {
        setState((prev) => ({ ...prev, step: prev.step + 1 }));
      };
    }

    if (state.step === 2) {
      disabled = isNonWalkInBookingValid(state.booking);
      if (waitlisted) {
        text = "Add to waitlist";
        disabled = false; // Ensure waitlisted bookings can be saved
      }
    }

    return {
      text,
      visible: true,
      disabled,
      onClick
    };
  };

  const primaryButton = getPrimaryButton();
  const secondaryButton = getSecondaryButton();

  return (
    <div className="flex flex-col w-[var(--sidebarWidth)] p-3 px-4 box-border h-full overflow-auto relative">
      {fullScreen}
      <div
        className={[
          "flex-1 flex flex-col overflow-x-hidden overflow-y-hidden pb-2.5 relative",
          fullScreen ? "hidden" : ""
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="flex opacity-100 justify-between items-center w-[305px] gap-2.5 pr-1.5 -mr-1.5 overflow-visible relative z-[1]">
          {state.step > 1 && (
            <EDSButton
              variant="ghost"
              size="icon"
              onClick={() => {
                setState((prev) => ({
                  ...prev,
                  step: Math.max(1, prev.step - 1)
                }));
              }}
              aria-label="Back"
            >
              <ChevronLeft />
            </EDSButton>
          )}
          <h2 className="text-left font-bold text-base text-[var(--primaryColor)] m-0 flex-grow mt-0.5 [[data-theme='Light']_&]:text-black">
            Add Booking
            {state.maxSteps > 1 && ` (${state.step}/${state.maxSteps})`}
          </h2>
          <EDSButton
            variant="ghost"
            size="icon"
            onClick={() => {
              bookingContext.setDiaryContext({
                gridAreaClick: undefined,
                highlightArea: undefined
              });
              setShowAddBooking(false);
            }}
            aria-label="Close"
            className="shrink-0 relative z-[2] -mr-0.5"
          >
            <X />
          </EDSButton>
        </div>
        <div
          className={["h-full", state.step === 1 ? "block" : "hidden"]
            .filter(Boolean)
            .join(" ")}
        >
          <AddBookingStep1
            booking={state.booking}
            updateBooking={updateBooking}
            handleWaitlistResponse={handleWaitlistResponse}
            setFullScreen={setFullScreen}
            onWaitlistAdded={() => {
              setState((prev) => ({
                ...prev,
                maxSteps: 2,
                step: prev.step === 1 ? 2 : prev.step
              }));
            }}
          />
        </div>
        <div
          className={["h-full", state.step === 2 ? "block" : "hidden"]
            .filter(Boolean)
            .join(" ")}
        >
          <AddBookingStep2
            booking={state.booking}
            updateBooking={updateBooking}
          />
        </div>
        <div className="flex justify-around">
          <div className="flex-none flex justify-between items-center w-full gap-4 pt-3 flex-col">
            {secondaryButton && secondaryButton.visible && (
              <EDSButton
                variant="outline"
                size="md"
                className="w-full"
                disabled={secondaryButton.disabled}
                onClick={secondaryButton.onClick}
              >
                {secondaryButton.text}
              </EDSButton>
            )}
            {primaryButton && primaryButton.visible && (
              <EDSButton
                variant="default"
                size="md"
                className="w-full"
                disabled={primaryButton.disabled}
                onClick={primaryButton.onClick}
              >
                {primaryButton.text}
              </EDSButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo<AddBookingType>(AddBooking);
